import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import invariant from 'tiny-invariant'
import * as styles from './quick-view-selector.css'
import { StyledExpander, StyledItemIndicator } from './styled-dropdown-menu'

export type QuickViewSelectItem<TId extends string> = {
  label: string
  value: TId
  count?: number
}

export type QuickViewSelectorProps<TId extends string> = {
  quickViews: readonly QuickViewSelectItem<TId>[]
  selectedQuickView: TId
  onSelectedQuickViewChange: (view: TId) => unknown
  currentCount?: number
}

export const QuickViewSelector = <TId extends string>({
  quickViews,
  selectedQuickView,
  onSelectedQuickViewChange,
  currentCount,
}: QuickViewSelectorProps<TId>) => {
  const selectedView = quickViews.find(view => view.value === selectedQuickView)
  invariant(selectedView, 'the selected quick view must appear in the list of quick views')

  const handleValueChange = (value: string) => {
    onSelectedQuickViewChange(value as TId)
  }

  const displayCount = currentCount ?? selectedView.count

  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger className={styles.trigger}>
        <span className={styles.viewingLabel} aria-hidden="true">
          Viewing:
        </span>
        <div className={styles.triggerContent}>
          <span className={styles.triggerLabel}>{selectedView.label}</span>
          {displayCount !== undefined && <span className={styles.triggerCount}>({displayCount})</span>}
          <StyledExpander />
        </div>
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content onCloseAutoFocus={e => e.preventDefault()} className={styles.content}>
          <RadixDropdownMenu.RadioGroup
            value={selectedQuickView}
            onValueChange={handleValueChange}
            className={styles.radioGroup}
          >
            {quickViews.map(view => (
              <QuickViewSelectorRadioItem key={view.value} view={view} />
            ))}
          </RadixDropdownMenu.RadioGroup>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}

const QuickViewSelectorRadioItem = <TId extends string>({ view }: { view: QuickViewSelectItem<TId> }) => {
  return (
    <RadixDropdownMenu.RadioItem className={styles.radioItem} value={view.value} disabled={view.count === 0}>
      <span className={styles.radioItemLabel}>{view.label}</span>
      {view.count !== undefined && <span className={styles.radioItemCount}>({view.count})</span>}
      <StyledItemIndicator />
    </RadixDropdownMenu.RadioItem>
  )
}
